import { memo, useCallback, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { ErrorBoundary } from '@src/components/ErrorBoundary';
import { useDeleteConfirmation } from '@src/hooks/useDeleteConfirmation';
import { usePredictionsContext } from '@src/providers/PredictionsProvider';
import type { PredictionEventRef } from '@src/types/PredictionMarket';
import {
  getPredictionEventForCardConfig,
  isPredictionMarketEventTradable,
  toPredictionEventRef,
} from '@src/types/PredictionMarket';
import {
  Button,
  ButtonVariants,
  CardViewEnum,
  ConnectionType,
  DropdownItem,
  DropdownMenu,
  FormControlSizes,
  IconButton,
  IconName,
  LoaderSizes,
  LoaderTalos,
  MarketDataCardState,
  MixpanelEvent,
  MixpanelEventSource,
  Text,
  useMarketsContext,
  useMixpanel,
  useSecurity,
  VStack,
  type PredictionMarketCardConfig,
} from '@talos/kyoko';

import { PredictionCardControls } from './PredictionCardControls';
import { PredictionCardFooter } from './PredictionCardFooter';
import { PredictionCardHeader } from './PredictionCardHeader';
import { PredictionCardSelector } from './PredictionCardSelector';
import { PredictionCardSettings } from './PredictionCardSettings';
import {
  PredictionOutcomePriceSubscriber,
  type PredictionOutcomePriceSummary,
} from './PredictionOutcomePriceSubscriber';
import { PredictionOutcomeRow } from './PredictionOutcomeRow';
import {
  buildPredictionMarketCardConfig,
  getPredictionCardMarketDataScope,
  resolveInitialPredictionMarket,
  resolvePredictionCardLogoMarket,
} from './predictionCardMarkets';
import {
  getBoundedPredictionOutcomePageIndex,
  getPredictionOutcomePageCount,
  getPredictionOutcomePageRows,
  shouldPaginatePredictionOutcomes,
} from './predictionCardPagination';
import { getPredictionCardQuantity } from './predictionCardQuantity';
import {
  shouldSortPredictionOutcomeRowsByOdds,
  sortPredictionOutcomeRowsByOdds,
  sortPredictionOutcomesByDisplaySymbol,
} from './predictionOutcomeSorting';
import { Body, CardContent, OutcomesBody, Wrapper } from './styles';
import type { PredictionCardFormValues } from './usePredictionCardForm';
import { usePredictionOutcomePrices } from './usePredictionOutcomePrices';

export interface PredictionCardProps {
  config: PredictionMarketCardConfig;
  isDraggable: boolean;
  isDragged: boolean;
  onDelete: (config: PredictionMarketCardConfig) => void;
  onSave: (config: PredictionMarketCardConfig) => void;
  onHidePlaceholders: () => void;
}

export const PredictionCard = memo(function PredictionCard({
  config,
  isDraggable,
  isDragged,
  onDelete,
  onSave,
  onHidePlaceholders,
}: PredictionCardProps) {
  const mixpanel = useMixpanel(MixpanelEventSource.PredictionMarketCard);
  const isNewCard = config.state === MarketDataCardState.NEW_CARD;
  const [showSettings, setShowSettings] = useState(false);
  const [isVisibleRef, isVisible] = useInView();
  const { eventsByID, getOutcomesForPredictionEvent, refDataIndexes, isRefDataReady } = usePredictionsContext();
  const { confirmDelete } = useDeleteConfirmation();

  const createInitialValues = useMemo((): PredictionCardFormValues => {
    const supportedMarkets = refDataIndexes.supportedMarkets;
    return {
      market: resolveInitialPredictionMarket(config.market, supportedMarkets),
      eventSeries: undefined,
      event: undefined,
      outcome: undefined,
      selectedMarketAccounts: [],
      selectedAggregation: undefined,
      view: CardViewEnum.NORMAL,
    };
  }, [config.market, refDataIndexes.supportedMarkets]);

  const [setupValues, setSetupValues] = useState<PredictionCardFormValues>(createInitialValues);

  const view = !isNewCard && config.outcomeSymbol ? CardViewEnum.COMPACT : CardViewEnum.NORMAL;
  const isCompact = !showSettings && view === CardViewEnum.COMPACT;

  const resolvedEvent = getPredictionEventForCardConfig(config, eventsByID);

  // A closed or resolved event can no longer be traded, so a saved card on one only offers removal.
  // Gated on ref data so a card does not flash the warning while events are still loading.
  const isEventUnavailable =
    !isNewCard && isRefDataReady && resolvedEvent != null && !isPredictionMarketEventTradable(resolvedEvent);

  const event = useMemo((): PredictionEventRef => {
    if (resolvedEvent) {
      return toPredictionEventRef(resolvedEvent);
    }

    return {
      EventID: config.eventID,
      EventSymbol: config.eventSymbol,
      EventName: config.eventTitle,
    };
  }, [config.eventID, config.eventSymbol, config.eventTitle, resolvedEvent]);

  const outcomeSecurities = useMemo(() => {
    if (config.outcomeSymbol) {
      return [];
    }
    return sortPredictionOutcomesByDisplaySymbol(getOutcomesForPredictionEvent(event));
  }, [config.outcomeSymbol, event, getOutcomesForPredictionEvent]);

  const singleOutcomeSecurity = useSecurity(config.outcomeSymbol);

  const isSingleOutcome = !!config.outcomeSymbol;
  const cardSecurities = useMemo(
    () => (singleOutcomeSecurity ? [singleOutcomeSecurity] : outcomeSecurities),
    [outcomeSecurities, singleOutcomeSecurity]
  );
  const quantity = useMemo(
    () => getPredictionCardQuantity(config.quantity, cardSecurities),
    [cardSecurities, config.quantity]
  );

  const headerTitle = isNewCard
    ? (setupValues.event?.EventName ?? 'Select event')
    : isSingleOutcome
      ? (singleOutcomeSecurity?.DisplaySymbol ?? config.outcomeSymbol ?? event.EventName)
      : event.EventName;
  const headerSubtitle = isSingleOutcome ? event.EventName : (setupValues.event?.Category ?? resolvedEvent?.Category);

  const rows = useMemo(() => {
    if (config.outcomeSymbol && singleOutcomeSecurity) {
      return [
        {
          symbol: singleOutcomeSecurity.Symbol,
          label: singleOutcomeSecurity.DisplaySymbol ?? singleOutcomeSecurity.Symbol,
        },
      ];
    }
    return outcomeSecurities.map(security => ({
      symbol: security.Symbol,
      label: security.DisplaySymbol ?? security.Symbol,
    }));
  }, [config.outcomeSymbol, outcomeSecurities, singleOutcomeSecurity]);

  const statusSymbol = rows[0]?.symbol;
  // A saved card carries its venue on the config; a card still in setup follows the picker.
  const configuredMarket = isNewCard ? setupValues.market : config.market;
  const logoMarket = resolvePredictionCardLogoMarket(configuredMarket, config.outcomeSymbol ?? config.eventSymbol);
  const marketDataScope = useMemo(() => getPredictionCardMarketDataScope(config), [config]);
  const { isMarketSupported } = useMarketsContext();
  const isRfqSupported = config.market != null && isMarketSupported(config.market, ConnectionType.RFQ);

  const { marketDataSnapshots } = usePredictionOutcomePrices(statusSymbol, `${config.id}-status`, {
    marketAccounts: marketDataScope.marketAccounts,
  });

  const [pricesBySymbol, setPricesBySymbol] = useState<ReadonlyMap<string, PredictionOutcomePriceSummary>>(
    () => new Map()
  );

  const handlePricesChange = useCallback((symbol: string, prices: PredictionOutcomePriceSummary) => {
    setPricesBySymbol(prev => {
      const existing = prev.get(symbol);
      if (
        existing &&
        existing.chancePct === prices.chancePct &&
        existing.yesAskPrice === prices.yesAskPrice &&
        existing.yesBidPrice === prices.yesBidPrice
      ) {
        return prev;
      }
      const next = new Map(prev);
      next.set(symbol, prices);
      return next;
    });
  }, []);

  const shouldSortByOdds = !isSingleOutcome && shouldSortPredictionOutcomeRowsByOdds(rows.length);

  // Sort outcomes by chance % (highest first) for smaller events; single-outcome cards keep their lone row as-is.
  const sortedRows = useMemo(() => {
    if (!shouldSortByOdds) {
      return rows;
    }
    const chanceBySymbol = new Map(Array.from(pricesBySymbol, ([symbol, prices]) => [symbol, prices.chancePct]));
    return sortPredictionOutcomeRowsByOdds(rows, chanceBySymbol);
  }, [pricesBySymbol, rows, shouldSortByOdds]);

  const isTradeLayout = isSingleOutcome && !isNewCard;
  const showFooter = !isNewCard && !isEventUnavailable && rows.length > 0;
  // An empty row set means "not loaded yet" until the securities stream and prediction ref data land.
  const isLoadingRefData = !isNewCard && rows.length === 0 && !isRefDataReady;
  const shouldPaginate = !isSingleOutcome && showFooter && shouldPaginatePredictionOutcomes(rows.length, isCompact);
  const pageCount = getPredictionOutcomePageCount(rows.length, isCompact);
  const pageIndex = getBoundedPredictionOutcomePageIndex(config.activePageIndex, pageCount);

  const visibleRows = useMemo(() => {
    if (!shouldPaginate) {
      return sortedRows;
    }
    return getPredictionOutcomePageRows(sortedRows, pageIndex, isCompact);
  }, [isCompact, pageIndex, sortedRows, shouldPaginate]);

  // When sorting by chance %, subscribe to every outcome so the order stays correct across pages.
  // Otherwise only the visible page needs prices.
  const subscribedRows = shouldSortByOdds ? rows : visibleRows;
  const priceSubscribers = subscribedRows.map(row => (
    <PredictionOutcomePriceSubscriber
      key={`price-${row.symbol}`}
      symbol={row.symbol}
      subscriptionTag={`${config.id}-${row.symbol}`}
      marketAccounts={marketDataScope.marketAccounts}
      quantity={quantity}
      onPricesChange={handlePricesChange}
    />
  ));

  const outcomeRowElements = visibleRows.map(row => {
    const prices = pricesBySymbol.get(row.symbol);
    return (
      <PredictionOutcomeRow
        key={row.symbol}
        symbol={row.symbol}
        label={row.label}
        compact={isCompact}
        showLabel={!isSingleOutcome}
        isRfqSupported={isRfqSupported}
        marketAccounts={marketDataScope.marketAccounts}
        aggregation={config.selectedAggregation}
        orderPresetID={config.orderPresetID}
        quantity={quantity}
        subAccount={config.subAccount}
        prices={prices}
      />
    );
  });

  const handleToggleSettings = useCallback(() => {
    mixpanel.track(showSettings ? MixpanelEvent.CloseCardSettings : MixpanelEvent.OpenCardSettings);
    setShowSettings(!showSettings);
  }, [mixpanel, showSettings]);

  const handleDeleteCard = useCallback(() => {
    const deleteCard = () => {
      onDelete(config);
      if (isNewCard) {
        onHidePlaceholders();
      }
    };

    if (isNewCard) {
      deleteCard();
      return;
    }

    confirmDelete({
      title: 'Delete Prediction Card',
      itemName: `"${headerTitle}"`,
      onConfirm: deleteCard,
    });
  }, [confirmDelete, config, headerTitle, isNewCard, onDelete, onHidePlaceholders]);

  // The event is gone, so the card holds nothing worth confirming away — same as a market data card
  // whose symbol cannot be found.
  const handleDeleteUnavailableCard = useCallback(() => {
    onDelete(config);
  }, [config, onDelete]);

  const handleSetupValuesChange = useCallback((values: PredictionCardFormValues) => {
    setSetupValues(values);
  }, []);

  const handleAddCard = useCallback(
    (values: PredictionCardFormValues) => {
      const { event } = values;
      if (!event) {
        return;
      }
      onSave(buildPredictionMarketCardConfig(config, { ...values, event }));
    },
    [config, onSave]
  );

  const handleSettingsDelete = useCallback(
    (card: PredictionMarketCardConfig) => {
      onDelete(card);
      setShowSettings(false);
    },
    [onDelete]
  );

  const handleSettingsSave = useCallback(
    (nextCard: PredictionMarketCardConfig) => {
      onSave(nextCard);
      setShowSettings(false);
    },
    [onSave]
  );

  const handlePreviousPage = useCallback(() => {
    onSave({ ...config, activePageIndex: Math.max(0, pageIndex - 1) });
  }, [config, onSave, pageIndex]);

  const handleNextPage = useCallback(() => {
    onSave({ ...config, activePageIndex: Math.min(pageCount - 1, pageIndex + 1) });
  }, [config, onSave, pageCount, pageIndex]);

  return (
    <Wrapper
      ref={isVisibleRef}
      isDragged={isDragged}
      $isSetup={isNewCard}
      $showSettings={showSettings}
      $view={view}
      data-testid={isNewCard ? 'prediction-card-new' : `prediction-card-${config.eventSymbol}`}
    >
      {isVisible ? (
        <ErrorBoundary>
          {showSettings ? (
            <PredictionCardSettings
              config={config}
              onClose={handleToggleSettings}
              onSave={handleSettingsSave}
              onDelete={handleSettingsDelete}
            />
          ) : (
            <CardContent>
              <PredictionCardHeader
                isDraggable={isDraggable}
                title={headerTitle}
                subtitle={headerSubtitle}
                statusSnapshots={statusSymbol ? marketDataSnapshots : undefined}
                selectedMarkets={marketDataScope.markets}
                selectedMarketAccounts={marketDataScope.marketAccounts}
                logoMarket={logoMarket}
                menu={
                  <DropdownMenu
                    usePortal
                    triggerElement={
                      <IconButton
                        data-testid="prediction-card-menu"
                        ghost
                        size={FormControlSizes.Small}
                        dim
                        icon={IconName.DotsVertical}
                      />
                    }
                    trigger="click"
                    placement="bottom-end"
                    dropdownProps={{ style: { width: '220px' } }}
                  >
                    {!isNewCard ? (
                      <DropdownItem startIcon={IconName.Cog} onClick={handleToggleSettings}>
                        Edit Card
                      </DropdownItem>
                    ) : null}
                    <DropdownItem startIcon={IconName.Trash} onClick={handleDeleteCard}>
                      Delete Card
                    </DropdownItem>
                  </DropdownMenu>
                }
              />
              <Body
                $isSetup={isNewCard}
                $fillsBody={isNewCard || isTradeLayout || showFooter || isLoadingRefData || isEventUnavailable}
              >
                {isNewCard ? (
                  <PredictionCardSelector
                    initialValues={createInitialValues}
                    onValuesChange={handleSetupValuesChange}
                    onCancel={handleDeleteCard}
                    onCreateCard={handleAddCard}
                  />
                ) : isLoadingRefData ? (
                  <LoaderTalos
                    size={LoaderSizes.SMALL}
                    delay={150}
                    text="Loading data"
                    dataTestID="prediction-card-loading"
                  />
                ) : isEventUnavailable ? (
                  <VStack gap="spacing8" h="100%" justifyContent="center" alignItems="center" px="spacing16">
                    <Text color="colorTextMuted" size="fontSizeSmall" textAlign="center">
                      Event is no longer available
                    </Text>
                    <Button
                      onClick={handleDeleteUnavailableCard}
                      variant={ButtonVariants.Negative}
                      size={FormControlSizes.Small}
                      data-testid="prediction-card-event-unavailable-delete"
                    >
                      Delete Card
                    </Button>
                  </VStack>
                ) : rows.length === 0 ? (
                  <Text
                    color="colorTextMuted"
                    size="fontSizeSmall"
                    textAlign="center"
                    py="spacing16"
                    px="spacing16"
                    data-testid="prediction-card-no-outcomes"
                  >
                    No outcomes available for this event.
                  </Text>
                ) : (
                  <>
                    <PredictionCardControls
                      config={config}
                      quantity={quantity}
                      securities={cardSecurities}
                      onSave={onSave}
                    />
                    {priceSubscribers}
                    {isTradeLayout ? outcomeRowElements : <OutcomesBody>{outcomeRowElements}</OutcomesBody>}
                    {showFooter ? (
                      <PredictionCardFooter
                        pageIndex={pageIndex}
                        pageCount={pageCount}
                        onPreviousPage={handlePreviousPage}
                        onNextPage={handleNextPage}
                      />
                    ) : null}
                  </>
                )}
              </Body>
            </CardContent>
          )}
        </ErrorBoundary>
      ) : null}
    </Wrapper>
  );
});
